import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { sendLoginMobileCodeAction } from '@ivymobi/orbit-core/business/user';
import { showNotification } from '@ivymobi/orbit-core/business/notification';
import { cn } from '../lib/utils';
import SendCodeButton from './components/SendCodeButton';
import CountryCodeSelect from './components/CountryCodeSelect';

interface SignUpPageProps {
  signup: (data: any) => void;
}

export default function SignUpPage({ signup }: SignUpPageProps) {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [countryCode, setCountryCode] = useState('+86');
  const [phone, setPhone] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 使用原有 showNotification 组件
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'error') => {
    dispatch(showNotification(message, type));
  }, [dispatch]);

  const doSendCode = useCallback((captchaToken: string) => {
    return (dispatch(sendLoginMobileCodeAction({ mobile: phone, countryCode, captchaToken })) as any)
      .then(
        () => showToast(t('Send Code Success'), 'success'),
        (err: any) => showToast(t(err.message, err.message) || '发送失败', 'error')
      );
  }, [phone, countryCode, dispatch, t, showToast]);

  const handleSignup = useCallback(() => {
    if (!phone || !verifyCode) {
      showToast('请填写完整信息');
      return;
    }
    if (phone.length < 8) {
      showToast('请输入正确的手机号');
      return;
    }
    setSubmitting(true);
    signup({ mobile: phone, code: verifyCode, countryCode });
    setTimeout(() => setSubmitting(false), 3000);
  }, [phone, verifyCode, countryCode, signup]);

  const inputClass = cn(
    'tw-w-full tw-h-11 tw-px-3 tw-rounded-md tw-text-sm',
    'tw-transition-colors tw-outline-none'
  );
  const inputStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'white',
  };

  return (
    <div className="tw-w-full">
      {/* 返回登录 */}
      <Link
        to="/user/signin"
        className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-no-underline tw-mb-5 tw-transition-colors"
        style={{ color: 'rgba(191, 219, 254, 0.6)' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgb(34, 211, 238)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(191, 219, 254, 0.6)'; }}
      >
        <ArrowLeft className="tw-w-4 tw-h-4" />
        {t('login.phoneToLogin', '返回登录')}
      </Link>

      <h3 className="tw-text-lg tw-font-semibold tw-text-white tw-mb-6">
        {t('login.freeRegister', '免费注册')}
      </h3>

      <div className="tw-space-y-4">
        {/* 国家代码 + 手机号 */}
        <div className="tw-space-y-2">
          <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
            {t('YBT-ADMIN.YBT-ADMIN-030101.YBT-ADMIN-03010107', '手机号')}
          </label>
          <div className="tw-flex tw-gap-2">
            <CountryCodeSelect
              value={countryCode}
              onChange={setCountryCode}
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('YBT-ADMIN.YBT-ADMIN-030103.YBT-ADMIN-03010320', '请输入手机号')}
              className={cn(inputClass, 'tw-flex-1')}
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgb(34, 211, 238)';
                e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* 验证码 */}
        <div className="tw-space-y-2">
          <label className="tw-text-sm" style={{ color: 'rgba(191, 219, 254, 0.7)' }}>
            {t('share.form.code', '验证码')}
          </label>
          <div className="tw-flex tw-gap-3">
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              placeholder={t('login.verificationCodePlaceholder', '请输入验证码')}
              className={cn(inputClass, 'tw-flex-1')}
              style={inputStyle}
              autoComplete="off"
              onFocus={(e) => {
                e.target.style.borderColor = 'rgb(34, 211, 238)';
                e.target.style.boxShadow = '0 0 0 2px rgba(34, 211, 238, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSignup();
              }}
            />
            <SendCodeButton
              disabled={!phone || phone.length < 8}
              mobile={phone}
              countryCode={countryCode}
              doSendCode={doSendCode}
            />
          </div>
        </div>

        {/* 注册按钮 */}
        <button
          type="button"
          onClick={handleSignup}
          disabled={submitting}
          className={cn(
            'tw-w-full tw-h-11 tw-mt-2 tw-rounded-md tw-text-sm tw-font-medium',
            'tw-border-none tw-cursor-pointer tw-transition-all tw-duration-200',
            submitting && 'tw-opacity-70 tw-cursor-not-allowed'
          )}
          style={{
            background: 'linear-gradient(to right, #2563eb, #06b6d4)',
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
          }}
          onMouseEnter={(e) => {
            if (!submitting) {
              (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3b82f6, #22d3ee)';
            }
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.background = 'linear-gradient(to right, #2563eb, #06b6d4)';
          }}
        >
          {submitting ? '注册中...' : t('login.freeRegister', '免费注册')}
        </button>
      </div>

      {/* 底部链接 */}
      <div className="tw-mt-6 tw-flex tw-items-center tw-gap-3">
        <Link
          to="/user/wechat"
          className="tw-text-sm tw-no-underline tw-transition-colors"
          style={{ color: 'rgba(191, 219, 254, 0.6)' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'rgb(34, 211, 238)'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(191, 219, 254, 0.6)'; }}
        >
          {t('login.wechat', '微信登录')}
        </Link>
        <span style={{ color: 'rgba(191, 219, 254, 0.3)' }}>|</span>
        <Link
          to="/user/signin"
          className="tw-text-sm tw-no-underline tw-transition-colors"
          style={{ color: 'rgba(191, 219, 254, 0.6)' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'rgb(34, 211, 238)'; }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'rgba(191, 219, 254, 0.6)'; }}
        >
          {t('login.phoneToLogin', '已有账号？登录')}
        </Link>
      </div>

      {/* 内置 Toast 暂时隐藏，使用原有 showNotification 组件 */}
    </div>
  );
}
